import {KidSettings} from '@/bank/bank.types'

export class KidGenerator {
    private kidSettings: KidSettings

    constructor(kidSettings: KidSettings) {
        this.kidSettings = kidSettings
    }

    generate(customerNo: string, invoiceNo: string): string {
        const digits = this.digits(customerNo, this.kidSettings.customerNoLength)
            .concat(this.digits(invoiceNo, this.kidSettings.invoiceNoLength))

        const controlDigit = this.kidSettings.modulus === 10
            ? KidGenerator.mod10ControlDigit(digits)
            : this.kidSettings.modulus === 11
                ? KidGenerator.mod11ControlDigit(digits)
                : (() => { throw new Error(`Unsupported modulus: ${this.kidSettings.modulus}`) })()

        return digits.join('') + controlDigit
    }

    private digits(input: string, length: number): number[] {
        const filtered = input.replace(/\D/g, '')
        const padded = filtered.length < length
            ? filtered.padStart(length, '0')
            : filtered.slice(-length)

        return Array.from(padded).map(char => parseInt(char, 10))
    }

    static mod10ControlDigit(input: number[]): string {
        const weights = [2, 1]

        const sum = input.slice().reverse().reduce((acc, digit, index) => {
            const product = digit * weights[index % weights.length]
            return acc + Array.from(product.toString()).reduce((sum, char) => sum + parseInt(char, 10), 0)
        }, 0) % 10

        return sum === 0 ? '0' : (10 - sum).toString()
    }

    static mod11ControlDigit(input: number[]): string {
        const weights = [2, 3, 4, 5, 6, 7]

        const sum = input.slice().reverse().reduce((acc, digit, index) => {
            return acc + digit * weights[index % weights.length]
        }, 0) % 11

        if (sum === 0) return '0'
        if (sum === 1) return '-'
        return (11 - sum).toString()
    }

    static isValid(kid: string): boolean {
        const controlDigit = kid.slice(-1)
        const body = kid.slice(0, -1).split('').map(char => parseInt(char, 10))

        const mod10ControlDigit = KidGenerator.mod10ControlDigit(body)
        const mod11ControlDigit = KidGenerator.mod11ControlDigit(body)

        return controlDigit === mod10ControlDigit || controlDigit === mod11ControlDigit
    }
}